import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import { 
  FileText, 
  Shield, 
  LayoutDashboard, 
  ChevronRight,
  Users,
  Home,
  IndianRupee,
  Wallet,
  Zap,
  CalendarDays,
  CalendarCheck,
  FileBarChart,
  Upload,
  UserCog,
  CheckCircle2,
  Info,
  Scale,
  Database,
  Camera,
  FileSignature,
  Settings,
  Save,
  ShieldCheck,
  Lock,
  AlertTriangle
} from 'lucide-react';

const HelpLegal = () => {
  const [activeTab, setActiveTab] = useState('facilities');

  const facilities = [
    {
      icon: LayoutDashboard,
      title: 'Dashboard & Analytics',
      features: [
        'Real-time statistics (guests, occupancy, vacant beds)',
        'Financial summary (receipts, payments, net balance)',
        'Receipt vs Payment trend charts',
        'Cash vs Bank breakdown analytics'
      ]
    },
    {
      icon: Home,
      title: 'Room Management',
      features: [
        'Add rooms with customizable sharing (1-6 beds)',
        'Individual bed rent configuration',
        'Visual occupancy status (color-coded)',
        'Room-wise reporting'
      ]
    },
    {
      icon: Users,
      title: 'Guest Management',
      features: [
        'Complete guest registration & profiles',
        'Status management (Joined, Notice, Left)',
        'Room shift with pro-rated calculations',
        'Due date change with transition handling',
        'Settlement processing on leaving'
      ]
    },
    {
      icon: CalendarCheck,
      title: 'Advance Booking',
      features: [
        'Reserve beds for future guests',
        'Collect advance deposits',
        'Convert bookings to active guests',
        'Cancel with deposit handling'
      ]
    },
    {
      icon: IndianRupee,
      title: 'Receipts',
      features: [
        'Record money received from guests',
        'Types: Rent, Deposit, Electricity',
        'Cash & Bank payment modes',
        'Auto-sync with Daily Hisab'
      ]
    },
    {
      icon: Wallet,
      title: 'Payments',
      features: [
        'Record business expenses',
        'Categories: Kitchen, Salary, Maintenance, etc.',
        'Cash & Bank tracking',
        'Category-wise analytics'
      ]
    },
    {
      icon: Zap,
      title: 'Electricity Management',
      features: [
        'Monthly meter reading records',
        'Auto-calculate per-bed charges',
        'Pro-rated billing (30-day standard)',
        'Notice guest estimation'
      ]
    },
    {
      icon: CalendarDays,
      title: 'Daily Hisab',
      features: [
        'Daily/Monthly/Lifetime views',
        'Receipt & Payment records',
        'Net balance calculation',
        'Guest type breakdown'
      ]
    },
    {
      icon: FileBarChart,
      title: 'Reports & Export',
      features: [
        'Monthly financial reports',
        'Outstanding dues report',
        'Excel export (Guests, Receipts, Payments)',
        'Settlement tracking'
      ]
    },
    {
      icon: Upload,
      title: 'Data Import',
      features: [
        'Bulk import from Excel',
        'Guests, Receipts, Payments, Electricity',
        'Validation & error reporting',
        'Duplicate detection'
      ]
    },
    {
      icon: Camera,
      title: 'Guest KYC & ID Verification',
      features: [
        'Capture guest photo + ID front/back (rear camera on mobile)',
        'Secure cloud object storage (auth-protected, no public URLs)',
        'KYC status tracking per guest',
        'Documents retained for compliance (no accidental delete)'
      ]
    },
    {
      icon: FileSignature,
      title: 'Registration Form (PDF)',
      features: [
        '2-page A4 Guest Registration Form with business header',
        'Faint logo watermark on every page',
        'Justified hostel rules & terms',
        'Digital signature pad captured before download'
      ]
    },
    {
      icon: Settings,
      title: 'Organisation Settings',
      features: [
        'Business details, GST, opening balances (org-wide)',
        'Customizable Guest ID prefix (one-time lock)',
        'One-tap starter template of 20 PG rules',
        'Editable hostel rules with paragraph justification'
      ]
    },
    {
      icon: Save,
      title: 'Backup & Restore',
      features: [
        'Full JSON backup of ALL data (financial + non-financial)',
        'Captures invoices, receipts, shifts & numbering counters',
        'One-click restore that rebuilds data exactly as it was',
        'Download & store your backups regularly'
      ]
    },
    {
      icon: ShieldCheck,
      title: 'Security',
      features: [
        'Role-based access control (Admin/Manager/Accountant/Viewer)',
        'Brute-force protection (5 failed attempts locks login)',
        'Strict CORS lockdown & secure JWT sessions',
        'All data transmitted over HTTPS + full audit trail'
      ]
    },
    {
      icon: UserCog,
      title: 'User Management',
      features: [
        'Role-based access (Admin, Manager, Accountant, Viewer)',
        'Create & manage users',
        'Permission control',
        'Activity tracking'
      ]
    }
  ];

  const termsOfUse = [
    {
      title: '1. Acceptance of Terms',
      content: 'By accessing and using the Stay Point PG Management System, you agree to be bound by these Terms of Use. If you do not agree to these terms, please do not use the Dashboard.'
    },
    {
      title: '2. User Accounts & Access',
      content: 'Users must be authorized by the system administrator. Each user is assigned a specific role (Admin, Manager, Accountant, or Viewer) with corresponding permissions. Users are responsible for maintaining the confidentiality of their login credentials.'
    },
    {
      title: '3. Permitted Use',
      content: 'The system may be used for: Managing PG property operations, Recording guest information and payments, Generating business reports, Tracking electricity consumption and billing, Managing room allocations and bookings.'
    },
    {
      title: '4. Prohibited Use',
      content: 'Users must not: Access data beyond their authorized role, Attempt to bypass security measures, Use the system for any illegal purpose, Share sensitive guest information with unauthorized parties, Modify or delete records without proper authorization.'
    },
    {
      title: '5. Data Entry Responsibility',
      content: 'Users are responsible for the accuracy of data they enter. Verify guest information before recording. Double-check financial entries before submission. Report any data discrepancies to the administrator.'
    },
    {
      title: '6. Financial Transactions',
      content: 'All receipts must be recorded with accurate date, amount, and payment mode. Cash and bank transactions must be categorized correctly. Receipts are automatically synced with Daily Hisab. Guest IDs and invoice/receipt numbers are system-generated and must not be manually altered.'
    },
    {
      title: '7. Guest KYC & Identity Documents',
      content: 'Guest photos and ID documents (front/back) are collected only for legitimate PG operations and identity verification. They are stored securely in access-controlled cloud storage with no public links. Users must obtain the guest\u2019s consent before capturing documents and must not share, download, or misuse them. For compliance safety, KYC documents cannot be casually deleted.'
    },
    {
      title: '8. Digital Signature & Registration Form',
      content: 'The 2-page Registration Form PDF includes the guest\u2019s digital signature and agreement to the hostel rules displayed in Organisation Settings. The signature is captured with the guest\u2019s consent and serves as their acknowledgement of the terms and house rules at the time of registration.'
    },
    {
      title: '9. Data Backup Responsibility',
      content: 'The system provides a Backup & Restore facility that lets you download a complete copy of your organisation\u2019s data at any time. You are solely responsible for taking backups regularly and storing them securely. We strongly recommend downloading a backup before any bulk import, restore, or major data change.'
    },
    {
      title: '10. Limitation of Liability & Data-Loss Disclaimer',
      content: 'The Dashboard is provided "as is" without warranties of any kind. We are NOT responsible or liable for any loss, corruption, or deletion of data \u2014 whether caused by user error, accidental deletion, incorrect restore, browser/device issues, or unforeseen technical failures. Users are responsible for verifying the accuracy of system-generated reports and for taking their own backups regularly. Please take a backup before making major changes.'
    },
    {
      title: '11. Modifications',
      content: 'We reserve the right to modify these terms at any time. Users will be notified of significant changes. Continued use after changes constitutes acceptance of new terms.'
    }
  ];

  const dataResponsibility = [
    {
      title: '1. Data Categories',
      content: 'Guest Personal Data (Name, Mobile, ID Proof, KYC Photo & ID documents) - High sensitivity. Financial Data (Invoices, Receipts, Payments, Deposits) - Medium sensitivity, 7-year retention. Property Data (Rooms, Beds) - Low sensitivity.'
    },
    {
      title: '2. Data Collection Principles',
      content: 'Minimization: Collect only necessary data. Accuracy: Verify information at registration. Consent: Inform guests about data practices and obtain consent before capturing KYC documents and digital signatures. Timeliness: Record transactions same day.'
    },
    {
      title: '3. Role-Based Access',
      content: 'Admin: Full access to all data, backups and restore. Manager: Guest and room data access. Accountant: Financial data access. Viewer: Read-only access to permitted sections.'
    },
    {
      title: '4. Data Security',
      content: 'Secure password requirements. Login brute-force protection (5 failed attempts lock the account). Strict CORS lockdown and secure JWT sessions. KYC documents stored in access-controlled cloud storage with no public links. All data transmitted over HTTPS. Audit trail for all modifications.'
    },
    {
      title: '5. Backup & Restore Responsibility',
      content: 'Admins can download a complete backup (all financial and non-financial data) at any time and restore it exactly as it was. Backups are your responsibility \u2014 download them regularly and store them securely offline. Always take a backup before bulk imports, restores, or major changes. A restore replaces existing data, so verify you are uploading the correct backup file.'
    },
    {
      title: '6. Data Retention',
      content: 'Financial records: 7 years (tax compliance). Personal data & KYC documents: 3 years after departure. Contact information: 1 year after departure. Active guest data: Throughout stay.'
    },
    {
      title: '7. Data Breach Protocol',
      content: 'Immediately notify administrator. Document the nature and extent. Secure affected systems. Assess impact on guest data. Notify affected parties if required.'
    },
    {
      title: '8. Data Export Guidelines',
      content: 'Export only necessary data. Store exports and backups securely. Delete exports when no longer needed. Do not share via unsecured channels.'
    },
    {
      title: '9. User Responsibilities',
      content: 'Access only data necessary for your role. Do not share login credentials. Log out after each session. Report suspicious activities immediately. Protect printed reports and downloaded backups. Take regular backups of your data.'
    }
  ];

  return (
    <div className="space-y-6" data-testid="help-legal-page">
      <div>
        <h1 className="text-3xl font-bold text-slate-800">Help & Legal</h1>
        <p className="text-slate-500 mt-1">System documentation, terms, and policies</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-3 mb-6">
          <TabsTrigger value="facilities" className="flex items-center gap-2" data-testid="facilities-tab">
            <LayoutDashboard className="h-4 w-4" />
            Facilities
          </TabsTrigger>
          <TabsTrigger value="terms" className="flex items-center gap-2" data-testid="terms-tab">
            <Scale className="h-4 w-4" />
            Terms of Use
          </TabsTrigger>
          <TabsTrigger value="data" className="flex items-center gap-2" data-testid="data-tab">
            <Database className="h-4 w-4" />
            Data Responsibility
          </TabsTrigger>
        </TabsList>

        {/* Facilities Tab */}
        <TabsContent value="facilities" className="space-y-6">
          <Card className="dash-animate bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200" style={{ animationDelay: '0ms' }}>
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-blue-100 rounded-lg">
                  <Info className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-blue-900">Stay Point PG Management System</h3>
                  <p className="text-blue-700 mt-1">
                    A comprehensive solution for managing your Paying Guest accommodations. 
                    Track guests, rooms, finances, and generate detailed reports - all in one place.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {facilities.map((facility, index) => (
              <Card key={index} className="dash-animate hover:shadow-md hover:-translate-y-0.5 transition-all duration-200" style={{ animationDelay: `${60 + index * 50}ms` }}>
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-3 text-lg">
                    <div className="p-2 bg-slate-100 rounded-lg">
                      <facility.icon className="h-5 w-5 text-slate-700" />
                    </div>
                    {facility.title}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {facility.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm text-slate-600">
                        <CheckCircle2 className="h-4 w-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Calculation Standards */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Calculation Standards
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                  <h4 className="font-semibold text-amber-800 mb-2">30-Day Standard Month</h4>
                  <p className="text-sm text-amber-700">
                    All pro-rated calculations use a 30-day month standard for consistency.
                  </p>
                  <code className="block mt-2 p-2 bg-amber-100 rounded text-xs font-mono">
                    daily_rate = monthly_rent / 30
                  </code>
                </div>
                <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                  <h4 className="font-semibold text-emerald-800 mb-2">Applied To</h4>
                  <ul className="text-sm text-emerald-700 space-y-1">
                    <li>• Rent pro-rating for mid-month joiners</li>
                    <li>• Electricity billing calculations</li>
                    <li>• Room shift adjustments</li>
                    <li>• Due date change transitions</li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Terms of Use Tab */}
        <TabsContent value="terms" className="space-y-4">
          <Card className="bg-gradient-to-r from-slate-50 to-slate-100 border-slate-200">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-slate-200 rounded-lg">
                  <Scale className="h-6 w-6 text-slate-700" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900">Terms of Use</h3>
                  <p className="text-slate-600 mt-1">
                    Please read these terms carefully before using the Stay Point Dashboard.
                    By using this system, you agree to be bound by these terms.
                  </p>
                  <p className="text-xs text-slate-500 mt-2">Last Updated: June 2026</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <ScrollArea className="h-[600px]">
            <div className="space-y-4 pr-4">
              {termsOfUse.map((term, index) => (
                <Card key={index}>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                      <ChevronRight className="h-4 w-4 text-blue-600" />
                      {term.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-slate-600 leading-relaxed">{term.content}</p>
                  </CardContent>
                </Card>
              ))}

              {/* User Roles Table */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <ChevronRight className="h-4 w-4 text-blue-600" />
                    User Roles & Permissions
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-2 font-semibold">Role</th>
                          <th className="text-left py-2 font-semibold">Access Level</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="border-b">
                          <td className="py-2"><span className="px-2 py-1 bg-red-100 text-red-700 rounded text-xs font-medium">Admin</span></td>
                          <td className="py-2 text-slate-600">Full access - all CRUD, reports, user management</td>
                        </tr>
                        <tr className="border-b">
                          <td className="py-2"><span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-medium">Manager</span></td>
                          <td className="py-2 text-slate-600">View all, edit guests, edit payments, view reports</td>
                        </tr>
                        <tr className="border-b">
                          <td className="py-2"><span className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs font-medium">Accountant</span></td>
                          <td className="py-2 text-slate-600">View financial, edit payments, view/export reports</td>
                        </tr>
                        <tr>
                          <td className="py-2"><span className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs font-medium">Viewer</span></td>
                          <td className="py-2 text-slate-600">Read-only access to permitted sections</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </div>
          </ScrollArea>
        </TabsContent>

        {/* Data Responsibility Tab */}
        <TabsContent value="data" className="space-y-4">
          <Card className="bg-gradient-to-r from-emerald-50 to-teal-50 border-emerald-200">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-emerald-100 rounded-lg">
                  <Shield className="h-6 w-6 text-emerald-700" />
                </div>
                <div>
                  <h3 className="font-semibold text-emerald-900">Data Responsibility Policy</h3>
                  <p className="text-emerald-700 mt-1">
                    This policy outlines responsibilities and guidelines for handling data within Stay Point.
                    All users must adhere to ensure data integrity and security.
                  </p>
                  <p className="text-xs text-emerald-600 mt-2">Last Updated: June 2026</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Data-Loss Disclaimer (prominent) */}
          <Card className="border-red-300 bg-red-50" data-testid="data-loss-disclaimer">
            <CardContent className="p-5">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-red-100 rounded-lg">
                  <AlertTriangle className="h-6 w-6 text-red-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-red-900">Important: Take Backups Regularly</h3>
                  <p className="text-sm text-red-700 mt-1 leading-relaxed">
                    We are <strong>NOT responsible for any data loss</strong> &mdash; whether caused by user error,
                    accidental deletion, an incorrect restore, browser/device issues, or unforeseen technical failures.
                    Please <strong>download a backup regularly</strong> from Organisation Settings &rarr; Data Backup and store it
                    securely. Always take a backup <strong>before bulk imports, restores, or major changes</strong>.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <ScrollArea className="h-[600px]">
            <div className="space-y-4 pr-4">
              {dataResponsibility.map((item, index) => (
                <Card key={index}>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                      <ChevronRight className="h-4 w-4 text-emerald-600" />
                      {item.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-slate-600 leading-relaxed">{item.content}</p>
                  </CardContent>
                </Card>
              ))}

              {/* Data Retention Table */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <ChevronRight className="h-4 w-4 text-emerald-600" />
                    Data Retention Summary
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-2 font-semibold">Data Type</th>
                          <th className="text-left py-2 font-semibold">Sensitivity</th>
                          <th className="text-left py-2 font-semibold">Retention Period</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="border-b">
                          <td className="py-2">Financial Records</td>
                          <td className="py-2"><span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded text-xs">Medium</span></td>
                          <td className="py-2 text-slate-600">7 years (tax compliance)</td>
                        </tr>
                        <tr className="border-b">
                          <td className="py-2">Guest Personal Data</td>
                          <td className="py-2"><span className="px-2 py-0.5 bg-red-100 text-red-700 rounded text-xs">High</span></td>
                          <td className="py-2 text-slate-600">Stay duration + 3 years</td>
                        </tr>
                        <tr className="border-b">
                          <td className="py-2">Contact Information</td>
                          <td className="py-2"><span className="px-2 py-0.5 bg-red-100 text-red-700 rounded text-xs">High</span></td>
                          <td className="py-2 text-slate-600">1 year after departure</td>
                        </tr>
                        <tr className="border-b">
                          <td className="py-2">Property Data</td>
                          <td className="py-2"><span className="px-2 py-0.5 bg-green-100 text-green-700 rounded text-xs">Low</span></td>
                          <td className="py-2 text-slate-600">Indefinite</td>
                        </tr>
                        <tr>
                          <td className="py-2">Electricity Records</td>
                          <td className="py-2"><span className="px-2 py-0.5 bg-green-100 text-green-700 rounded text-xs">Low</span></td>
                          <td className="py-2 text-slate-600">3 years</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </div>
          </ScrollArea>
        </TabsContent>
      </Tabs>

      {/* Footer */}
      <Card className="bg-slate-900 text-white">
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div>
              <h3 className="font-bold text-lg">Stay Point</h3>
              <p className="text-slate-400 text-sm">PG Management System</p>
            </div>
            <div className="text-center md:text-right">
              <p className="text-slate-400 text-sm">For support, contact:</p>
              <p className="text-white">admin@staypointindia.com</p>
            </div>
          </div>
          <div className="mt-4 pt-4 border-t border-slate-700 text-center text-xs text-slate-500">
            © 2026 Stay Point Private Limited. All Rights Reserved.
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default HelpLegal;
